import SlsTracker from '@aliyun-sls/web-track-browser'
import dayjs from 'dayjs'
import { useUserStore } from '@/store/modules/user'

// SLS 配置
const opts = {
  host: 'ap-southeast-1.log.aliyuncs.com', // 所在地域的服务入口
  project: 'smogo-app', // Project名称
  logstore: 'console', // Logstore名称
  time: 2, // 发送日志的时间间隔，默认是10秒
  count: 20 // 发送日志的数量大小，默认是10条
}

const tracker = new SlsTracker(opts)

/**
 * 发送日志消息
 * @param type 日志级别
 * @param title 日志标题
 * @param msg 日志消息
 * @param extra 额外信息
 */
const sendMsg = (type: string, title: string, msg?: string, extra?: any) => {
  try {
    const userStore = useUserStore()

    tracker.send({
      env: import.meta.env.VITE_API_BASE_PATH,
      time: dayjs().format('YYYY-MM-DD HH:mm:ss.SSS'),
      level: type,
      msg: msg || title,
      title: msg ? title : '',
      device: {
        userAgent: navigator.userAgent,
        language: navigator.language,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        screenAvailWidth: window.screen.availWidth,
        screenAvailHeight: window.screen.availHeight
      },
      route: {
        href: window.location.href,
        path: window.location.hash
          ? window.location.hash.split('#')[1]?.split('?')[0]
          : window.location.pathname
      },
      user: userStore.userInfo || {},
      extra: extra || {}
    })
  } catch (error) {
    console.error('日志发送失败:', error)
  }
}

/**
 * 日志工具类
 */
const logger = {
  /**
   * 信息日志
   * @param title 标题
   * @param msg 消息
   * @param extra 额外信息
   */
  info: (title: string, msg?: string, extra?: any) => {
    sendMsg('info', title, msg, extra)
  },

  /**
   * 错误日志
   * @param title 标题
   * @param msg 消息
   * @param extra 额外信息
   */
  error: (title: string, msg?: string, extra?: any) => {
    sendMsg('error', title, msg, extra)
  },

  /**
   * 警告日志
   * @param title 标题
   * @param msg 消息
   * @param extra 额外信息
   */
  warn: (title: string, msg?: string, extra?: any) => {
    sendMsg('warn', title, msg, extra)
  },

  /**
   * 调试日志
   * @param title 标题
   * @param msg 消息
   * @param extra 额外信息
   */
  debug: (title: string, msg?: string, extra?: any) => {
    sendMsg('debug', title, msg, extra)
  },

  /**
   * 记录 API 请求
   * @param config 请求配置
   * @param response 响应数据
   * @param error 错误信息
   */
  apiRequest: (config: any, response?: any, error?: any) => {
    const logData = {
      url: config.url,
      method: config.method?.toUpperCase(),
      params: config.params,
      data: config.data,
      headers: config.headers,
      response: response
        ? {
            status: response.status,
            data: response.data,
            headers: response.headers
          }
        : undefined,
      error: error
        ? {
            message: error.message,
            status: error.response?.status,
            data: error.response?.data
          }
        : undefined,
      duration: config._requestStartTime ? Date.now() - config._requestStartTime : undefined
    }

    if (error) {
      sendMsg('error', 'API请求失败', `${config.method?.toUpperCase()} ${config.url}`, logData)
    } else {
      sendMsg('info', 'API请求成功', `${config.method?.toUpperCase()} ${config.url}`, logData)
    }
  },

  /**
   * 记录路由跳转
   * @param to 目标路由
   * @param from 来源路由
   */
  routeChange: (to: any, from: any) => {
    const logData = {
      to: {
        path: to.path,
        name: to.name,
        params: to.params,
        query: to.query,
        meta: to.meta
      },
      from: {
        path: from.path,
        name: from.name,
        params: from.params,
        query: from.query,
        meta: from.meta
      }
    }

    sendMsg('info', '路由跳转', `从 ${from.path} 跳转到 ${to.path}`, logData)
  }
}

export default logger
